/*
 * qi-designer.js — QI LITE visual designer v2 (checkout-ready PDPs)
 * --------------------------------------------------------------------------
 * A lightweight multi-element design studio (CustomInk-style, scaled down):
 *   - Upload logo image(s)            (type:'image')
 *   - Add Text  (font / color / size) (type:'text')
 *   - Add Art   (built-in clipart)    (type:'art')
 * Each element can be selected, dragged, resized, rotated, layered and deleted.
 * On approve, the whole stage is flattened to ONE PNG proof and handed to the
 * existing add-to-cart flow. No backend — proof blob is written to a hidden file
 * input via DataTransfer so Shopify stores it on the order line item.
 *
 * Form inputs written:  properties[_proof] (file, flattened),
 *                        properties[_logo_original] (file, first uploaded image),
 *                        properties[upload_logo] (file, same original),
 *                        properties[_placement] (text, JSON of every element).
 */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024;
  var OK_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg'];

  var FONTS = [
    { name: 'Clean', css: 'Arial, Helvetica, sans-serif' },
    { name: 'Serif', css: 'Georgia, "Times New Roman", serif' },
    { name: 'Mono', css: '"Courier New", monospace' },
    { name: 'Round', css: '"Trebuchet MS", "Segoe UI", sans-serif' },
    { name: 'Bold', css: 'Impact, "Arial Black", sans-serif' }
  ];

  // built-in clipart — single-color SVG paths (use currentColor → recolorable)
  var ART = [
    { n: 'Star', p: 'M12 2l3 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.9 21l1.2-6.8-5-4.9 6.9-1z' },
    { n: 'Heart', p: 'M12 21s-7-4.7-9.3-9.3C1 8 2.7 5 5.8 5 7.7 5 9 6 12 9c3-3 4.3-4 6.2-4C21.3 5 23 8 21.3 11.7 19 16.3 12 21 12 21z' },
    { n: 'Circle', p: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z' },
    { n: 'Bolt', p: 'M13 2L4 14h6l-1 8 9-12h-6z' },
    { n: 'Leaf', p: 'M5 21c0-9 7-16 16-16 0 9-7 16-16 16zm2-3c6-1 10-5 11-11-6 1-10 5-11 11z' },
    { n: 'Cup', p: 'M4 3h13v6a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V3zm13 2h2a3 3 0 0 1 0 6h-2V5zM3 20h16v2H3z' },
    { n: 'Shield', p: 'M12 2l8 3v6c0 5-3.4 9-8 11-4.6-2-8-6-8-11V5l8-3z' },
    { n: 'Paw', p: 'M5.5 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm5-3a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4zm6 0a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4zm2 3a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 13c-3 0-6 2.3-6 5 0 1.7 1.5 2.5 3 2 1-.4 2-.6 3-.6s2 .2 3 .6c1.5.5 3-.3 3-2 0-2.7-3-5-6-5z' },
    { n: 'Smile', p: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM8.5 9.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm7 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zM7.5 14h9a4.5 4.5 0 0 1-9 0z' },
    { n: 'Crown', p: 'M3 7l4 4 5-7 5 7 4-4-2 12H5L3 7z' },
    { n: 'Sun', p: 'M12 6a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM12 1v3M12 20v3M1 12h3M20 12h3M4 4l2 2M18 18l2 2M20 4l-2 2M6 18l-2 2' },
    { n: 'Flag', p: 'M5 3v18H3V3h2zm2 0h12l-3 4 3 4H7V3z' }
  ];

  function svgDataURL(pathOrSvg, color) {
    var inner = pathOrSvg.indexOf('<') === 0 ? pathOrSvg
      : '<path d="' + pathOrSvg + '"/>';
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="' + color + '" stroke="' + color + '" stroke-width="0">' + inner + '</svg>';
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }

  function el(id) { return document.getElementById(id); }
  function stage() { return el('qi-designer-stage'); }
  function layer() { return el('qi-designer-layer'); }

  // ---- state -------------------------------------------------------------
  var S = { els: [], sel: null, seq: 0, firstImageFile: null };
  var nodes = {}; // id -> DOM node

  function showError(msg) {
    var e = el('qi-designer-error');
    if (!e) return;
    if (msg) { e.textContent = msg; e.hidden = false; } else { e.hidden = true; e.textContent = ''; }
  }

  // ---- open / close (top-most overlay, scroll lock) ----------------------
  // sync the designer's product image to whatever color/variant is currently
  // selected in the PDP gallery, so the proof matches what the customer picked
  function syncProductImage() {
    var main = document.getElementById('productPhotoImg');
    var pimg = el('qi-designer-product-img');
    if (!main || !pimg) return;
    var src = main.currentSrc || main.src || main.getAttribute('data-zoom');
    if (src && pimg.src !== src) { pimg.src = src; }
  }
  function open() {
    var ov = el('qi-designer-overlay');
    if (!ov) return;
    syncProductImage();
    if (ov.parentNode !== document.body) document.body.appendChild(ov);
    ov.hidden = false;
    ov.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
  }
  function close() {
    var ov = el('qi-designer-overlay');
    if (!ov) return;
    ov.hidden = true;
    ov.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
  }
  function onKey(e) {
    var ccOv = el('qi-confirm-overlay');
    if (e.key === 'Escape' && ccOv && !ccOv.hidden) { hideConfirm(); return; }
    if (e.key === 'Escape') { if (S.sel) { select(null); } else { close(); } }
    else if ((e.key === 'Delete' || e.key === 'Backspace') && S.sel && !isTyping(e)) { e.preventDefault(); removeEl(S.sel); }
  }
  function isTyping(e) { var t = e.target; return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT'); }

  // ---- element factory ---------------------------------------------------
  function add(elObj) {
    elObj.id = 'qe' + (++S.seq);
    elObj.z = S.els.length;
    S.els.push(elObj);
    render();
    select(elObj.id);
    syncEmptyHint();
    return elObj;
  }
  function find(id) { for (var i = 0; i < S.els.length; i++) if (S.els[i].id === id) return S.els[i]; return null; }

  function centerXY(w, h) {
    var s = stage(); var sw = s ? s.clientWidth : 400, sh = s ? s.clientHeight : 400;
    var off = (S.els.length % 6) * 16; // cascade so stacked adds don't perfectly overlap
    return { x: (sw - w) / 2 + off, y: (sh - h) / 2 + off };
  }

  function addImageFromFile(f) {
    showError('');
    if (OK_TYPES.indexOf(f.type) === -1) { showError('Please upload a PNG, SVG or JPG file.'); return; }
    if (f.size > MAX_BYTES) { showError('File is too large (max 20MB).'); return; }
    var reader = new FileReader();
    reader.onload = function () {
      var img = new Image();
      img.onload = function () {
        if (!S.firstImageFile) S.firstImageFile = f;
        var s = stage(); var sw = s ? s.clientWidth : 400;
        var w = sw * 0.4, aspect = (img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : 1;
        var h = w / aspect;
        var c = centerXY(w, h);
        add({ type: 'image', src: reader.result, img: img, aspect: aspect, x: c.x, y: c.y, w: w, h: h, rot: 0, file: f });
      };
      img.onerror = function () { showError('Could not read that image. Try a different file.'); };
      img.src = reader.result;
    };
    reader.onerror = function () { showError('Could not read that file.'); };
    reader.readAsDataURL(f);
  }

  function addText() {
    var fontSize = Math.round((stage() ? stage().clientWidth : 400) * 0.08);
    var c = centerXY(160, fontSize * 1.3);
    add({ type: 'text', text: 'Your Text', font: FONTS[0].css, color: '#222222', fontSize: fontSize, x: c.x, y: c.y, w: 160, h: fontSize * 1.3, rot: 0 });
    var ti = el('qi-prop-text'); if (ti) { ti.focus(); ti.select(); }
  }

  function addArt(idx) {
    var artDef = ART[idx]; if (!artDef) return;
    var s = stage(); var sw = s ? s.clientWidth : 400;
    var w = sw * 0.28, color = '#570f48';
    var c = centerXY(w, w);
    add({ type: 'art', path: artDef.p, color: color, aspect: 1, x: c.x, y: c.y, w: w, h: w, rot: 0 });
  }

  function removeEl(id) {
    S.els = S.els.filter(function (e) { return e.id !== id; });
    if (S.sel === id) S.sel = null;
    render(); selectionUI(); syncEmptyHint();
  }
  function bringForward(id) { reorder(id, 1); }
  function sendBack(id) { reorder(id, -1); }
  function reorder(id, dir) {
    var i = S.els.findIndex(function (e) { return e.id === id; });
    var j = i + dir;
    if (i < 0 || j < 0 || j >= S.els.length) return;
    var t = S.els[i]; S.els[i] = S.els[j]; S.els[j] = t;
    render(); selectionUI();
  }

  // ---- rendering ---------------------------------------------------------
  function elementInner(e) {
    if (e.type === 'image') return '<img src="' + e.src + '" alt="" draggable="false">';
    if (e.type === 'art') return '<img src="' + svgDataURL(e.path, e.color) + '" alt="" draggable="false">';
    return ''; // text handled separately (text node)
  }

  function applyStyle(node, e) {
    node.style.left = e.x + 'px';
    node.style.top = e.y + 'px';
    node.style.transform = 'rotate(' + e.rot + 'deg)';
    if (e.type === 'text') {
      node.style.width = 'auto';
      var tn = node.querySelector('.qi-el-text');
      if (tn) {
        tn.textContent = e.text || ' ';
        tn.style.fontFamily = e.font;
        tn.style.color = e.color;
        tn.style.fontSize = e.fontSize + 'px';
      }
      e.w = node.offsetWidth; e.h = node.offsetHeight;
    } else {
      node.style.width = e.w + 'px';
      node.style.height = (e.w / (e.aspect || 1)) + 'px';
      e.h = e.w / (e.aspect || 1);
    }
  }

  function render() {
    var lay = layer(); if (!lay) return;
    lay.innerHTML = '';
    nodes = {};
    S.els.forEach(function (e) {
      var node = document.createElement('div');
      node.className = 'qi-el qi-el-' + e.type + (S.sel === e.id ? ' sel' : '');
      node.setAttribute('data-id', e.id);
      if (e.type === 'text') {
        var tn = document.createElement('div');
        tn.className = 'qi-el-text';
        node.appendChild(tn);
      } else {
        node.innerHTML = elementInner(e);
      }
      if (S.sel === e.id) {
        node.innerHTML += ''; // keep
        ['rot', 'size', 'del'].forEach(function (h) {
          var hd = document.createElement('div');
          hd.className = 'qi-h qi-h-' + h;
          hd.setAttribute('data-h', h);
          if (h === 'del') hd.innerHTML = '&times;';
          node.appendChild(hd);
        });
      }
      lay.appendChild(node);
      nodes[e.id] = node;
      applyStyle(node, e);
    });
  }

  // ---- selection + properties panel -------------------------------------
  function select(id) {
    S.sel = id;
    render();
    selectionUI();
  }
  function selectionUI() {
    var e = S.sel ? find(S.sel) : null;
    var panel = el('qi-props');
    if (panel) panel.hidden = !e;
    var txtRow = el('qi-prop-text-row');
    if (txtRow) txtRow.hidden = !(e && e.type === 'text');
    var fontRow = el('qi-prop-font-row');
    if (fontRow) fontRow.hidden = !(e && e.type === 'text');
    var colorRow = el('qi-prop-color-row');
    if (colorRow) colorRow.hidden = !(e && (e.type === 'text' || e.type === 'art'));
    if (e) {
      if (e.type === 'text') {
        var ti = el('qi-prop-text'); if (ti) ti.value = e.text;
        var fs = el('qi-prop-font'); if (fs) fs.value = e.font;
      }
      if (e.type === 'text' || e.type === 'art') {
        var co = el('qi-prop-color'); if (co) co.value = toHex(e.color);
      }
    }
  }
  function toHex(c) { return (c && c.charAt(0) === '#') ? c : '#570f48'; }

  // ---- gestures (move / resize / rotate) ---------------------------------
  var g = null;
  function ptr(ev) { return { x: ev.clientX, y: ev.clientY }; }
  function stageRect() { return stage().getBoundingClientRect(); }

  function onLayerDown(ev) {
    var hEl = ev.target.closest ? ev.target.closest('.qi-h') : null;
    var elNode = ev.target.closest ? ev.target.closest('.qi-el') : null;
    if (hEl && elNode) {
      var id = elNode.getAttribute('data-id'); var e = find(id);
      var kind = hEl.getAttribute('data-h');
      if (kind === 'del') { removeEl(id); return; }
      select(id);
      startGesture(kind, e, ev);
      ev.preventDefault(); ev.stopPropagation();
      return;
    }
    if (elNode) {
      var id2 = elNode.getAttribute('data-id');
      if (S.sel !== id2) select(id2);
      startGesture('move', find(id2), ev);
      ev.preventDefault();
      return;
    }
    // empty stage → deselect
    select(null);
  }

  function startGesture(kind, e, ev) {
    var p = ptr(ev), r = stageRect();
    var h = e.type === 'text' ? e.h : e.w / (e.aspect || 1);
    var cx = r.left + e.x + e.w / 2, cy = r.top + e.y + h / 2;
    g = {
      kind: kind, e: e, px: p.x, py: p.y,
      ox: e.x, oy: e.y, ow: e.w, ofs: e.fontSize, orot: e.rot,
      cx: cx, cy: cy,
      startDist: Math.hypot(p.x - cx, p.y - cy),
      startAng: Math.atan2(p.y - cy, p.x - cx) * 180 / Math.PI
    };
    if (layer().setPointerCapture && ev.pointerId != null) { try { layer().setPointerCapture(ev.pointerId); } catch (x) {} }
  }

  function onMove(ev) {
    if (!g) return;
    var p = ptr(ev), e = g.e, node = nodes[e.id];
    if (g.kind === 'move') {
      e.x = g.ox + (p.x - g.px);
      e.y = g.oy + (p.y - g.py);
      clamp(e);
    } else if (g.kind === 'size') {
      var d = Math.hypot(p.x - g.cx, p.y - g.cy);
      var ratio = g.startDist > 4 ? d / g.startDist : 1;
      if (e.type === 'text') {
        e.fontSize = Math.max(8, Math.round(g.ofs * ratio));
      } else {
        var newW = Math.max(20, g.ow * ratio);
        var hh = newW / (e.aspect || 1);
        e.x = (g.ox + g.ow / 2) - newW / 2;
        e.y = (g.oy + (g.ow / (e.aspect || 1)) / 2) - hh / 2;
        e.w = newW;
      }
    } else if (g.kind === 'rot') {
      var ang = Math.atan2(p.y - g.cy, p.x - g.cx) * 180 / Math.PI;
      e.rot = Math.round(g.orot + (ang - g.startAng));
    }
    if (node) applyStyle(node, e);
    ev.preventDefault();
  }
  function onUp() { if (g) { g = null; } }

  function clamp(e) {
    var s = stage(); if (!s) return;
    var sw = s.clientWidth, sh = s.clientHeight, h = e.h || e.w;
    e.x = Math.max(-e.w * 0.6, Math.min(e.x, sw - e.w * 0.4));
    e.y = Math.max(-h * 0.6, Math.min(e.y, sh - h * 0.4));
  }

  // ---- empty hint --------------------------------------------------------
  function syncEmptyHint() {
    var hint = el('qi-designer-empty'); if (hint) hint.hidden = S.els.length > 0;
    var ap = el('qi-designer-approve'); if (ap) ap.disabled = S.els.length === 0;
    var cl = el('qi-designer-clear'); if (cl) cl.disabled = S.els.length === 0;
  }

  // wipe everything and start over
  function doClearAll() {
    S.els = []; S.sel = null; nodes = {}; S.firstImageFile = null;
    render(); selectionUI(); syncEmptyHint();
    var n = el('qi-upload-name'); if (n) n.textContent = 'No file chosen';
    var f = el('qi-designer-file'); if (f) f.value = '';
    showTool('upload');
  }
  function clearAll() {
    if (!S.els.length) return;
    var ov = el('qi-confirm-overlay');
    if (!ov) { doClearAll(); return; }
    ov.hidden = false;
    requestAnimationFrame(function () { ov.classList.add('show'); });
  }
  function hideConfirm() {
    var ov = el('qi-confirm-overlay'); if (!ov) return;
    ov.classList.remove('show');
    setTimeout(function () { ov.hidden = true; }, 160);
  }

  // ---- flatten to proof PNG ---------------------------------------------
  function loadImg(src) {
    return new Promise(function (res, rej) { var i = new Image(); i.onload = function () { res(i); }; i.onerror = rej; i.src = src; });
  }
  function generateProof() {
    var s = stage(); var prod = el('qi-designer-product-img');
    var sw = s.clientWidth, sh = s.clientHeight, F = 2;
    var c = document.createElement('canvas');
    c.width = sw * F; c.height = sh * F;
    var ctx = c.getContext('2d');
    ctx.scale(F, F);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, sw, sh);
    // product (object-fit: contain)
    var pa = (prod.naturalWidth && prod.naturalHeight) ? prod.naturalWidth / prod.naturalHeight : 1;
    var dw, dh, ox, oy;
    if (pa > sw / sh) { dw = sw; dh = sw / pa; ox = 0; oy = (sh - dh) / 2; }
    else { dh = sh; dw = sh * pa; ox = (sw - dw) / 2; oy = 0; }
    try { ctx.drawImage(prod, ox, oy, dw, dh); } catch (e) { return Promise.reject(e); }

    // elements in z-order (array order)
    var chain = Promise.resolve();
    S.els.forEach(function (e) {
      chain = chain.then(function () {
        var h = e.type === 'text' ? e.h : e.w / (e.aspect || 1);
        var ecx = e.x + e.w / 2, ecy = e.y + h / 2;
        if (e.type === 'text') {
          ctx.save();
          ctx.translate(ecx, ecy);
          ctx.rotate(e.rot * Math.PI / 180);
          ctx.fillStyle = e.color;
          ctx.font = e.fontSize + 'px ' + e.font;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(e.text || '', 0, 0);
          ctx.restore();
          return;
        }
        var src = e.type === 'art' ? svgDataURL(e.path, e.color) : e.src;
        return loadImg(src).then(function (img) {
          ctx.save();
          ctx.translate(ecx, ecy);
          ctx.rotate(e.rot * Math.PI / 180);
          ctx.drawImage(img, -e.w / 2, -h / 2, e.w, h);
          ctx.restore();
        });
      });
    });
    return chain.then(function () {
      return new Promise(function (res, rej) {
        if (c.toBlob) c.toBlob(function (b) { b ? res(b) : rej(new Error('toBlob null')); }, 'image/png');
        else { try { res(dataURLtoBlob(c.toDataURL('image/png'))); } catch (e) { rej(e); } }
      });
    });
  }
  function dataURLtoBlob(d) {
    var parts = d.split(','), mime = parts[0].match(/:(.*?);/)[1], bin = atob(parts[1]), n = bin.length, u8 = new Uint8Array(n);
    while (n--) u8[n] = bin.charCodeAt(n);
    return new Blob([u8], { type: mime });
  }

  function setFileInput(input, file) {
    if (!input || !file) return false;
    try { var dt = new DataTransfer(); dt.items.add(file); input.files = dt.files; return true; } catch (e) { return false; }
  }

  function placementJSON() {
    var s = stage();
    return JSON.stringify({
      v: 2, stageW: s ? s.clientWidth : null, stageH: s ? s.clientHeight : null,
      elements: S.els.map(function (e) {
        return {
          type: e.type, x: Math.round(e.x), y: Math.round(e.y), w: Math.round(e.w),
          h: Math.round(e.h || e.w), rotation: e.rot,
          text: e.text || undefined, font: e.font || undefined,
          color: e.color || undefined, fontSize: e.fontSize || undefined
        };
      })
    });
  }

  // Freeze the page while the proof renders and the item is added. Proof
  // generation is async and the add navigates, so without this the shopper can
  // click Add to Cart / Buy again in the gap and double-add.
  //
  // Only controls this function actually changed are restored afterwards --
  // tracked with data-qi-locked -- because several are legitimately disabled
  // already (e.g. #imprintColorId while the method is Blank) and blanket
  // re-enabling would resurrect them.
  function lockPage(on) {
    var overlay = el('qi-designer-overlay');
    // BUTTONS ONLY -- deliberately not inputs or selects. Disabled form
    // controls are omitted from a form POST, and submitCartForm() posts
    // #addToCartForm, so disabling them would strip quantity, imprint method,
    // delivery date and the proof file out of the add-to-cart request.
    var controls = document.querySelectorAll('button, input[type="submit"], input[type="button"]');
    var i, n;

    for (i = 0; i < controls.length; i++) {
      n = controls[i];
      // leave the designer's own UI alone: on failure the shopper must still be
      // able to close it or try again
      if (overlay && overlay.contains(n)) { continue; }
      // never disable the trigger this flow is about to click -- .click() on a
      // disabled button is a no-op, which would silently cancel the add
      if (n.id === 'addToCart') { continue; }
      if (on) {
        if (!n.disabled) { n.setAttribute('data-qi-locked', '1'); n.disabled = true; }
      } else if (n.getAttribute('data-qi-locked')) {
        n.disabled = false;
        n.removeAttribute('data-qi-locked');
      }
    }

    // anchors cannot be disabled; neutralise them instead
    var links = document.querySelectorAll('a[href], a[onclick]');
    for (i = 0; i < links.length; i++) {
      n = links[i];
      if (overlay && overlay.contains(n)) { continue; }
      if (on) {
        if (n.style.pointerEvents !== 'none') {
          n.setAttribute('data-qi-locked', '1');
          n.style.pointerEvents = 'none';
          n.setAttribute('aria-disabled', 'true');
        }
      } else if (n.getAttribute('data-qi-locked')) {
        n.style.pointerEvents = '';
        n.removeAttribute('aria-disabled');
        n.removeAttribute('data-qi-locked');
      }
    }

    if (typeof window.pageLoader === 'function') { window.pageLoader(on); }
  }

  function approve() {
    var ap = el('qi-designer-approve');
    if (!S.els.length) { showError('Add a logo, some text or art first.'); return; }
    if (ap) { ap.disabled = true; ap.classList.add('qi-designer-busy'); }
    lockPage(true);
    select(null); // hide handles so they aren't baked into the proof
    generateProof().then(function (blob) {
      var proofFile = new File([blob], 'qi-proof.png', { type: 'image/png' });
      var okProof = setFileInput(el('qi-proof-input'), proofFile);
      if (S.firstImageFile) { setFileInput(el('qi-logo-original'), S.firstImageFile); setFileInput(el('image-upload'), S.firstImageFile); }
      var pl = el('qi-placement-input'); if (pl) pl.value = placementJSON();
      close();
      var btn = el('addToCart'); if (btn) btn.click();
      if (!okProof && window.console) console.warn('[qi-designer] proof file could not be attached (DataTransfer unsupported); placement saved.');
    }).catch(function (e) {
      showError('Could not generate the proof. ' + (e && e.message ? e.message : ''));
      // nothing was added and we are not navigating -- give the page back
      lockPage(false);
      if (ap) { ap.disabled = false; ap.classList.remove('qi-designer-busy'); }
    });
  }

  // ---- build clipart + font UI ------------------------------------------
  function buildPalettes() {
    var artGrid = el('qi-art-grid');
    if (artGrid && !artGrid.childNodes.length) {
      ART.forEach(function (a, i) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'qi-art-item'; b.title = a.n;
        b.innerHTML = '<img src="' + svgDataURL(a.p, '#570f48') + '" alt="' + a.n + '">';
        b.addEventListener('click', function () { addArt(i); });
        artGrid.appendChild(b);
      });
    }
    var fontSel = el('qi-prop-font');
    if (fontSel && !fontSel.childNodes.length) {
      FONTS.forEach(function (f) {
        var o = document.createElement('option'); o.value = f.css; o.textContent = f.name;
        o.style.fontFamily = f.css; fontSel.appendChild(o);
      });
    }
  }

  // ---- tool drawers ------------------------------------------------------
  function showTool(which) {
    ['upload', 'text', 'art'].forEach(function (t) {
      var d = el('qi-tool-' + t); if (d) d.hidden = (t !== which);
      var b = el('qi-toolbtn-' + t); if (b) b.classList.toggle('active', t === which);
    });
  }

  function bind() {
    if (!el('qi-designer-overlay')) return; // not a checkout-ready PDP
    buildPalettes();

    // close
    var c = el('qi-designer-close'); if (c) c.addEventListener('click', close);
    var ov = el('qi-designer-overlay');
    if (ov) ov.addEventListener('click', function (e) { if (e.target === ov) close(); });

    // toolbar
    var tu = el('qi-toolbtn-upload'); if (tu) tu.addEventListener('click', function () { showTool('upload'); });
    var tt = el('qi-toolbtn-text'); if (tt) tt.addEventListener('click', function () { showTool('text'); addText(); });
    var ta = el('qi-toolbtn-art'); if (ta) ta.addEventListener('click', function () { showTool('art'); });

    // upload (browse + drag-and-drop)
    function setUploadName(name) { var n = el('qi-upload-name'); if (n) n.textContent = name || 'No file chosen'; }
    var f = el('qi-designer-file'); if (f) f.addEventListener('change', function (ev) {
      var file = ev.target.files && ev.target.files[0]; if (file) { setUploadName(file.name); addImageFromFile(file); } ev.target.value = '';
    });
    var dz = el('qi-dropzone');
    if (dz) {
      ['dragenter', 'dragover'].forEach(function (t) { dz.addEventListener(t, function (e) { e.preventDefault(); dz.classList.add('drag'); }); });
      ['dragleave', 'dragend'].forEach(function (t) { dz.addEventListener(t, function (e) { e.preventDefault(); dz.classList.remove('drag'); }); });
      dz.addEventListener('drop', function (e) {
        e.preventDefault(); dz.classList.remove('drag');
        var file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) { setUploadName(file.name); addImageFromFile(file); }
      });
    }

    // properties
    var ti = el('qi-prop-text'); if (ti) ti.addEventListener('input', function () { var e = find(S.sel); if (e && e.type === 'text') { e.text = ti.value; applyStyle(nodes[e.id], e); } });
    var fo = el('qi-prop-font'); if (fo) fo.addEventListener('change', function () { var e = find(S.sel); if (e && e.type === 'text') { e.font = fo.value; applyStyle(nodes[e.id], e); } });
    var co = el('qi-prop-color'); if (co) co.addEventListener('input', function () {
      var e = find(S.sel); if (!e) return;
      if (e.type === 'text' || e.type === 'art') {
        e.color = co.value;
        if (e.type === 'art') { var im = nodes[e.id] && nodes[e.id].querySelector('img'); if (im) im.src = svgDataURL(e.path, e.color); }
        else applyStyle(nodes[e.id], e);
      }
    });
    var del = el('qi-prop-del'); if (del) del.addEventListener('click', function () { if (S.sel) removeEl(S.sel); });
    var fwd = el('qi-prop-forward'); if (fwd) fwd.addEventListener('click', function () { if (S.sel) bringForward(S.sel); });
    var bwd = el('qi-prop-back'); if (bwd) bwd.addEventListener('click', function () { if (S.sel) sendBack(S.sel); });

    // clear + approve
    var cl = el('qi-designer-clear'); if (cl) cl.addEventListener('click', clearAll);
    var ap = el('qi-designer-approve'); if (ap) ap.addEventListener('click', approve);

    // clear-confirm dialog
    var ccOv = el('qi-confirm-overlay');
    var ccCancel = el('qi-confirm-cancel'); if (ccCancel) ccCancel.addEventListener('click', hideConfirm);
    var ccOk = el('qi-confirm-ok'); if (ccOk) ccOk.addEventListener('click', function () { hideConfirm(); doClearAll(); });
    if (ccOv) ccOv.addEventListener('click', function (e) { if (e.target === ccOv) hideConfirm(); });

    // stage gestures
    var lay = layer();
    if (lay) {
      lay.addEventListener('pointerdown', onLayerDown);
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    }

    showTool('upload');
    syncEmptyHint();
  }

  window.QIDesigner = {
    open: open, close: close, clear: clearAll,
    addText: addText, addArt: addArt,
    _addImageFromFile: addImageFromFile, _state: S, _proof: generateProof
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
